import React, { useState, useEffect } from "react";

import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";

import { esES } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { SocketContext, SocketManager } from './context/Socket/SocketContext';
import { WebInfoProvider, useWebInfo } from './context/WebInfoContext';
import logger from "./utils/logger";

import Routes from "./routes";
import ErrorBoundary from "./components/ErrorBoundary";
// import DragDropTest from "./components/DragDropTest";

const queryClient = new QueryClient();

const App = () => {
    return (
        <ErrorBoundary>
            <WebInfoProvider>
                <AppContent />
            </WebInfoProvider>
        </ErrorBoundary>
    );
};

// ✅ Componente interno que usa useWebInfo (debe estar dentro del provider)
const AppContent = () => {
    const [locale, setLocale] = useState();

    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
    const preferredTheme = window.localStorage.getItem("preferredTheme");
    const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");

    // ✅ Obtener información web desde DB (ahora desde contexto compartido)
    const { webInfo } = useWebInfo();

    // Log de prueba al cargar la aplicación
    useEffect(() => {
        logger.debug("🎯 App.js cargado - Aplicación iniciada");
        logger.whatsapp.debug("🧪 LOG DE PRUEBA - WhatsApp desde App.js");
        logger.socket.debug("🧪 LOG DE PRUEBA - Socket desde App.js");
    }, []);

    // ✅ Actualizar título cuando se carguen los datos desde DB (useWebInfo ya actualiza el título)
    // ✅ CORREGIDO: Usar websiteTitle directamente, no appName + appSubtitle
    useEffect(() => {
        if (webInfo?.title) {
            document.title = webInfo.title;
            const titleElement = document.getElementById('app-title');
            if (titleElement) {
                titleElement.textContent = webInfo.title;
            }
        }
    }, [webInfo]);

    const colorMode = React.useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
            },
        }),
        []
    );

    const theme = createTheme(
        {
            scrollbarStyles: {
                "&::-webkit-scrollbar": {
                    width: '8px',
                    height: '8px',
					borderRadius: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                    boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
                    backgroundColor: "#1e3a8a",
					borderRadius: "8px",
                },
            },
            scrollbarStylesSoft: {
                "&::-webkit-scrollbar": {
                    width: "8px",
					borderRadius: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: mode === "light" ? "#F3F4F6" : "#374151",
					borderRadius: "8px",
                },
            },
            palette: {
                type: mode,
                primary: { main: mode === "light" ? "#1e3a8a" : "#3b82f6" },
                secondary: { main: "#ea0000" }, // ✅ COLOR PERSONALIZADO PARA GRUPOS
				sair: { main: mode === "light" ? "#1e3a8a" : "#374151" },
				vcard: { main: mode === "light" ? "#1e3a8a" : "#6b7280" },
                textPrimary: mode === "light" ? "#1f2937" : "#f9fafb",
                borderPrimary: mode === "light" ? "#1e3a8a" : "#3b82f6",
                dark: { main: mode === "light" ? "#374151" : "#f3f4f6" },
                light: { main: mode === "light" ? "#f9fafb" : "#374151" },
                tabHeaderBackground: mode === "light" ? "#f3f4f6" : "#374151",
                optionsBackground: mode === "light" ? "#ffffff" : "#1f2937",
				options: mode === "light" ? "#ffffff" : "#374151",
				fontecor: mode === "light" ? "#1e3a8a" : "#3b82f6",
                fancyBackground: mode === "light" ? "#f9fafb" : "#111827",
				bordabox: mode === "light" ? "#e5e7eb" : "#374151",
				newmessagebox: mode === "light" ? "#f3f4f6" : "#374151",
				inputdigita: mode === "light" ? "#ffffff" : "#374151",
				contactdrawer: mode === "light" ? "#ffffff" : "#374151",
				announcements: mode === "light" ? "#f3f4f6" : "#374151",
				login: mode === "light" ? "#ffffff" : "#111827",
				announcementspopover: mode === "light" ? "#ffffff" : "#374151",
				chatlist: mode === "light" ? "#f3f4f6" : "#374151",
				boxlist: mode === "light" ? "#f3f4f6" : "#374151",
				boxchatlist: mode === "light" ? "#f3f4f6" : "#1f2937",
                total: mode === "light" ? "#ffffff" : "#111827",
                messageIcons: mode === "light" ? "#6b7280" : "#d1d5db",
                inputBackground: mode === "light" ? "#ffffff" : "#374151",
                barraSuperior: mode === "light" ? "linear-gradient(to right, #1e3a8a, #3b82f6, #1e3a8a)" : "#1f2937",
				boxticket: mode === "light" ? "#f3f4f6" : "#374151",
				campaigntab: mode === "light" ? "#f3f4f6" : "#374151",
				mediainput: mode === "light" ? "#f3f4f6" : "#111827",
				// ✅ NUEVOS COLORES PARA MENSAJES - TEMA DARK
				messageBackground: mode === "light" ? "#f5f5f5" : "#111827",
				messageLeftBg: mode === "light" ? "#93c5fd" : "#1e40af", // Azul más oscuro para dark
				messageLeftText: mode === "light" ? "#000000" : "#ffffff",
				messageRightBg: mode === "light" ? "#6ee7b7" : "#059669", // Verde más oscuro para dark
				messageRightText: mode === "light" ? "#000000" : "#ffffff",
				quotedLeftBg: mode === "light" ? "#f0f0f0" : "#374151",
				quotedRightBg: mode === "light" ? "#cfe9ba" : "#065f46",
				quotedSideLeft: mode === "light" ? "#6bcbef" : "#3b82f6",
				quotedSideRight: mode === "light" ? "#35cd96" : "#10b981",
				dailyTimestampBg: mode === "light" ? "#e1f3fb" : "#1f2937",
				dailyTimestampText: mode === "light" ? "#808888" : "#9ca3af",
				messageContactNameColor: mode === "light" ? "#6bcbef" : "#60a5fa",
				messageDeletedText: mode === "light" ? "rgba(0, 0, 0, 0.36)" : "rgba(255, 255, 255, 0.5)",
				forwardMessageColor: mode === "light" ? "#999" : "#9ca3af",
            },
            mode,
        },
        locale
    );

    useEffect(() => {
        const i18nlocale = localStorage.getItem("i18nextLng");
        const browserLocale =
            i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5);

        if (browserLocale === "esES") {
            setLocale(esES);
        }
    }, []);

    useEffect(() => {
        window.localStorage.setItem("preferredTheme", mode);
    }, [mode]);



    return (
        <ColorModeContext.Provider value={{ colorMode }}>
            <ThemeProvider theme={theme}>
                <QueryClientProvider client={queryClient}>
                  <SocketContext.Provider value={SocketManager}>
                      <Routes />
                      {/* <DragDropTest /> */}
                  </SocketContext.Provider>
                </QueryClientProvider>
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};

export default App;
